import { inject, Injectable } from '@angular/core';
import type { Observable } from 'rxjs';
import { delay, from, map, switchMap, throwError } from 'rxjs';

import { BleDeviceManagerService } from './BLE/ble-device-manager.service';
import { CannesWifiService } from './cannes-wifi.service';
import { SettingsService } from './settings.service';
import type { PersistedHuntingProduct } from '../../pages/hunting-products/models/hunting-product.model';

// Per Cannes wiki: 0x00 = Access Point mode (device hosts WiFi)
const WIFI_MODE_AP = 0x00;
// Device requires a false->true WifiEnabled edge to restart the AP with a new passphrase.
const WIFI_DISABLE_SETTLE_MS = 1_500;
// After WifiEnabled=true the device needs time to bring wlan0 up before the AP accepts connections.
// 12s is empirically reliable on Android 12+; the device CLI `sleep 3` is too optimistic in practice.
const AP_READY_DELAY_MS = 12_000;

@Injectable({
  providedIn: 'root',
})
export class CannesWifiConnectionService {
  private readonly bleDeviceManager = inject(BleDeviceManagerService);
  private readonly cannesWifiService = inject(CannesWifiService);
  private readonly settingsService = inject(SettingsService);

  /**
   * Provisions the Cannes AP and connects the phone to it.
   * Reuses an existing passphrase if it's spec-compliant, otherwise generates a new one.
   * Runs on every call because the device may have rebooted or disabled WiFi.
   */
  provisionAndConnect$(deviceId: string): Observable<string> {
    return from(this.resolveStablePassphrase(deviceId)).pipe(
      switchMap(passphrase =>
        this.bleDeviceManager.readAccessPointSSID$().pipe(
          switchMap(ssidRes => {
            const ssid = ssidRes.data;
            if (!ssid) {
              return throwError(() => new Error('Failed to read AP SSID'));
            }
            return this.bleDeviceManager.writeConnectivityEnabled$(true).pipe(
              switchMap(() => this.bleDeviceManager.writeWifiMode$(WIFI_MODE_AP)),
              switchMap(() => this.bleDeviceManager.writeAccessPointPassphrase$(passphrase)),
              switchMap(() => this.bleDeviceManager.writeWifiEnabled$(false)),
              delay(WIFI_DISABLE_SETTLE_MS),
              switchMap(() => this.bleDeviceManager.writeWifiEnabled$(true)),
              delay(AP_READY_DELAY_MS),
              switchMap(() => from(this.persistApPassphrase(deviceId, passphrase))),
              switchMap(() => from(this.cannesWifiService.connectToCannesAp(ssid, passphrase))),
              map(() => ssid)
            );
          })
        )
      )
    );
  }

  /**
   * The AP passphrase must stay STABLE across reconnects: once Android remembers the
   * WifiNetworkSpecifier approval it silently re-joins with the same credentials, so a rotated
   * passphrase breaks every reconnect. Reuse the device's stored passphrase; generate a new one only
   * when none is stored (persistApPassphrase saves it). Reads durable storage directly — the NgRx store
   * isn't updated when we persist mid-session, so reading it would be stale and force a rotating one.
   */
  private async resolveStablePassphrase(deviceId: string): Promise<string> {
    const stored = (await this.settingsService.getHuntingProduct$(deviceId))?.accessPointPassphrase;
    return this.cannesWifiService.isReusablePassphrase(stored) ? stored : this.cannesWifiService.generatePassphrase();
  }

  // Upsert: the passphrase must persist even on a device's first WiFi connect, before it exists in
  // storage. Skipping that case would re-introduce the passphrase rotation this flow exists to prevent.
  private async persistApPassphrase(deviceId: string, passphrase: string): Promise<void> {
    const base: PersistedHuntingProduct = (await this.settingsService.getHuntingProduct$(deviceId)) ?? {
      deviceId,
      productType: 'CANNES',
      lastSyncedAt: 0,
    };
    await this.settingsService.setHuntingProduct$({
      ...base,
      accessPointPassphrase: passphrase,
      lastSyncedAt: Date.now(),
    });
  }
}
